import { PAGE_SIZE_DEFAULT } from '@constants';
import { BaseResponse } from '@dto';
import { usePrevious } from '@dwarvesf/react-hooks';
import {
  useMutation,
  useQueryClient,
  QueryKey,
  QueryFunction,
  UseInfiniteQueryOptions,
  useQuery,
  UseQueryOptions,
  UseMutationOptions,
  MutateOptions,
  MutationKey,
  useSuspenseInfiniteQuery
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useUser } from '../auth';

export const useMutationEnhancer = <
  TData = unknown,
  TVariables = unknown,
  TContext = unknown,
  TError = unknown
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext> & {
    invalidateQueryKeys?: Array<MutationKey>;
  }
) => {
  const queryClient = useQueryClient();

  const { mutate, ...rest } = useMutation<TData, TError, TVariables, TContext>({
    ...options,
    onSuccess: (data, variables, context) => {
      // if (options.mutationKey) {
      //   queryClient.invalidateQueries({
      //     queryKey: options.mutationKey,
      //     type: 'active'
      //   });
      // }
      if (options.invalidateQueryKeys) {
        options.invalidateQueryKeys.forEach(key => {
          queryClient.invalidateQueries({
            queryKey: key,
            type: 'active'
          });
        });
      }
      options?.onSuccess?.(data, variables, context);
    }
  });

  const mutateEnhancer = useCallback(
    (
      v?: TVariables,
      o?: MutateOptions<TData, TError, TVariables, TContext> | undefined
    ) => {
      mutate(v as TVariables, o);
    },
    [mutate]
  );

  return {
    mutate: mutateEnhancer,
    ...rest
  };
};

export const useQueryEnhancer = <
  TData = unknown,
  TQueryFnData = unknown,
  TQueryKey extends QueryKey = QueryKey,
  TError = unknown
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> & {
    onSuccess?: (data?: TData) => void;
    onError?: (data: TError) => void;
  }
) => {
  const { queryFn, onSuccess, onError, ...rest } = options;
  const props = useQuery<TQueryFnData, TError, TData, TQueryKey>({
    ...rest,
    queryFn: async () => {
      const res: BaseResponse<TData> = await (queryFn as any)?.();

      if (res.success) {
        onSuccess?.(res.data);
        return res.data;
      }

      onError?.(res.error);
      return null as any;
    }
  });

  return { ...props, isLoading: props.isFetching };
};

export const useInfiniteEnhancer = <
  TData = unknown,
  TQueryParams = unknown,
  TQueryKey extends QueryKey = QueryKey,
  TError = unknown
>(
  options: Omit<
    UseInfiniteQueryOptions<
      TQueryParams,
      TError,
      TData,
      TQueryParams,
      TQueryKey
    >,
    'queryKey' | 'queryFn'
  > & {
    queryKey: TQueryKey;
    queryFn: QueryFunction<TQueryParams, TQueryKey>;
  }
) => {
  const { user } = useUser();
  const prevUser = usePrevious(user);

  const { queryFn, queryKey, initialPageParam = 0, ...restOptions } = options;
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();
  const {
    data,
    isFetchingNextPage,
    isRefetching,
    refetch,
    fetchNextPage,
    ...rest
  } = useSuspenseInfiniteQuery<TQueryParams, TError, TData, TQueryKey>({
    queryKey,
    queryFn,
    retry: false,
    initialPageParam,
    // @ts-ignore
    getNextPageParam: (lastPage: any, allPage) => {
      return lastPage.length < PAGE_SIZE_DEFAULT ? undefined : allPage.length;
    },
    ...restOptions
  });

  useEffect(() => {
    if (!isRefetching && isRefreshing) {
      setIsRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefetching]);

  const results = useMemo(() => {
    if ((data as any)?.pages) {
      return (data as any).pages.reduce((prev: TData[], curr: any) => {
        return prev.concat(curr);
      }, [] as TData[]);
    }
    return [];
  }, [data]);

  const onRefresh = useCallback(() => {
    queryClient.setQueriesData({ queryKey }, (allData: any) => {
      return {
        pageParams: [allData?.pageParams?.[0]],
        pages: [allData?.pages?.[0]]
      };
    });
    setIsRefreshing(true);
    refetch();
  }, [queryKey, queryClient, refetch]);

  useEffect(() => {
    if (prevUser?.id !== user?.id) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, prevUser?.id]);

  const onEndReached = useCallback(() => {
    if (!isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, isFetchingNextPage]);

  return {
    ...rest,
    isFetchingNextPage,
    data: results,
    isRefreshingEnhancer: isRefreshing,
    isRefetching,
    onRefreshEnhancer: onRefresh,
    onEndReached,
    fetchNextPage
  };
};
